import { getAuthConfig } from "@/config/auth";
import type { AuthService, AuthTokens } from "@/data/types/auth";
import { authStorage } from "@/services/auth/auth-storage/auth-storage";

const EXPIRY_BUFFER_S = 60;
const MIN_RETRY_DELAY_MS = 5_000;
const MAX_RETRY_DELAY_MS = 60_000;

/**
 * Authentication service implementing OAuth 2.0 Client Credentials Flow
 * This authenticates the APPLICATION itself, not individual users
 * WARNING: This implementation uses client_secret in browser code, which is NOT secure
 * This should NEVER be used in production. For demo purposes only.
 */
class AuthServiceImpl implements AuthService {
  private isAuthenticating = false;
  private refreshPromise: Promise<boolean> | null = null;
  private renewalTimer: ReturnType<typeof setTimeout> | null = null;
  private generation = 0;
  private retryCount = 0;
  private listenersAdded = false;

  constructor() {
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleOnline = this.handleOnline.bind(this);
  }

  /**
   * Get access token using Client Credentials flow
   * This authenticates the app itself, not a user
   */
  private async getClientCredentialsToken(): Promise<AuthTokens> {
    const authConfig = await getAuthConfig();
    if (!authConfig) {
      throw new Error(
        "API configuration not found. Please configure API settings first."
      );
    }

    const tokenEndpoint = authConfig.issuerBaseURL;

    const formData = new URLSearchParams();
    formData.append("grant_type", "client_credentials");
    formData.append("client_id", authConfig.clientId);
    formData.append("client_secret", authConfig.clientSecret);
    formData.append("scope", authConfig.scope);

    const response = await fetch(tokenEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
      body: formData.toString(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Authentication failed: ${response.status} ${response.statusText}. ` +
          `Error: ${errorData.error || errorData.error_description || "Unknown error"}`
      );
    }

    const tokenData = await response.json();

    const expiresAt =
      Math.floor(Date.now() / 1000) + (tokenData.expires_in || 3600);

    return {
      accessToken: tokenData.access_token,
      expiresAt: expiresAt,
      tokenType: tokenData.token_type || "Bearer",
      scope: tokenData.scope,
    };
  }

  // --- Proactive renewal scheduling ---

  private scheduleRenewal(tokens: AuthTokens): void {
    this.cancelRenewal();

    const now = Math.floor(Date.now() / 1000);
    const usableRemaining = tokens.expiresAt - now - EXPIRY_BUFFER_S;

    if (usableRemaining <= 0) {
      this.performScheduledRefresh();
      return;
    }

    // Renew at 75% of usable lifetime, capped to never exceed usableRemaining
    const delayS = Math.min(usableRemaining * 0.75, usableRemaining);
    this.renewalTimer = setTimeout(
      () => this.performScheduledRefresh(),
      delayS * 1000
    );
  }

  private async performScheduledRefresh(): Promise<void> {
    const gen = this.generation;
    const success = await this.refreshTokens();

    // If logout happened while refreshing, discard result
    if (this.generation !== gen) {
      return;
    }

    if (success) {
      this.retryCount = 0;
    } else {
      // Retry with exponential backoff if current token is still usable
      const tokens = await authStorage.getTokens();
      if (tokens && !authStorage.isTokenExpired(tokens)) {
        const delay = Math.min(
          MIN_RETRY_DELAY_MS * 2 ** this.retryCount,
          MAX_RETRY_DELAY_MS
        );
        this.retryCount++;
        this.renewalTimer = setTimeout(
          () => this.performScheduledRefresh(),
          delay
        );
      }
    }
  }

  private cancelRenewal(): void {
    if (this.renewalTimer !== null) {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
    }
  }

  /**
   * Start proactive token renewal and listen for resume/online events.
   * Call once after initial authentication succeeds.
   */
  startProactiveRenewal(): void {
    this.addEventListeners();
    this.checkAndSchedule();
  }

  /**
   * Stop proactive renewal and remove event listeners.
   */
  stopProactiveRenewal(): void {
    this.cancelRenewal();
    this.removeEventListeners();
  }

  private addEventListeners(): void {
    if (this.listenersAdded) {
      return;
    }
    this.listenersAdded = true;
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("online", this.handleOnline);
  }

  private removeEventListeners(): void {
    if (!this.listenersAdded) {
      return;
    }
    this.listenersAdded = false;
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
    window.removeEventListener("online", this.handleOnline);
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === "visible") {
      this.checkAndSchedule();
    }
  }

  private handleOnline(): void {
    this.checkAndSchedule();
  }

  private async checkAndSchedule(): Promise<void> {
    const tokens = await authStorage.getTokens();
    if (!tokens) {
      return;
    }

    if (authStorage.isTokenExpired(tokens)) {
      this.performScheduledRefresh();
      return;
    }

    this.scheduleRenewal(tokens);
  }

  // --- End proactive renewal ---

  /**
   * Ensure app is authenticated
   * Gets new token if none exists or if expired
   */
  async ensureAuthenticated(): Promise<boolean> {
    if (this.isAuthenticating) {
      return false;
    }

    try {
      this.isAuthenticating = true;

      const tokens = await authStorage.getTokens();

      if (tokens && !authStorage.isTokenExpired(tokens)) {
        this.scheduleRenewal(tokens);
        return true;
      }

      const newTokens = await this.getClientCredentialsToken();
      await authStorage.setTokens(newTokens);
      this.scheduleRenewal(newTokens);

      return true;
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes("API configuration not found")
      ) {
        return false;
      }
      console.error("Authentication failed:", error);
      throw error;
    } finally {
      this.isAuthenticating = false;
    }
  }

  /**
   * Refresh access token by getting a new one with client credentials.
   * Single-flight: concurrent callers share one in-flight request.
   */
  async refreshTokens(): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.doRefresh();
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async doRefresh(): Promise<boolean> {
    const gen = this.generation;
    const authConfig = await getAuthConfig();
    if (!authConfig) {
      return false;
    }

    try {
      const newTokens = await this.getClientCredentialsToken();

      if (this.generation !== gen) {
        return false;
      }

      await authStorage.setTokens(newTokens);
      this.scheduleRenewal(newTokens);
      return true;
    } catch (error) {
      console.error("Token refresh failed:", error);
      return false;
    }
  }

  /**
   * Initialize authentication on app start
   */
  async login(): Promise<void> {
    await this.ensureAuthenticated();
  }

  /**
   * Not applicable for client credentials flow
   * Kept for interface compatibility
   */
  async handleCallback(): Promise<void> {
    console.warn("handleCallback is not used in Client Credentials flow");
  }

  /**
   * Clear stored tokens and stop proactive renewal
   */
  async logout(): Promise<void> {
    this.generation++;
    this.stopProactiveRenewal();
    await authStorage.clearAll();
  }

  /**
   * Get stored tokens
   */
  async getStoredTokens(): Promise<AuthTokens | null> {
    return await authStorage.getTokens();
  }

  /**
   * Clear tokens
   */
  async clearTokens(): Promise<void> {
    await authStorage.clearAll();
  }

  /**
   * Check if tokens are expired
   */
  isTokenExpired(tokens: AuthTokens): boolean {
    return authStorage.isTokenExpired(tokens);
  }
}

// Export singleton instance
export const authService = new AuthServiceImpl();
